import React from "react";
import { useTranslation } from "react-i18next";
import { VisitOutcome } from "../../features/sessions/models/VisitOutcome";
import { RosterSection } from "../../features/sessions/utils/buildAttendanceRoster";
import { SessionVisits } from "../../features/sessions/visits-slice";
import AttendanceRow from "./AttendanceRow";

interface RosterAttendanceSectionProps {
  section: RosterSection;
  outcomes: SessionVisits;
  /** Member id → the name of their Visit's own Payment Type (e.g. a late-cancellation fee). */
  feeNames?: Record<string, string>;
  /** Members with a declared absence covering this occurrence's date. */
  awayMemberIds?: Set<string>;
  onChange: (memberId: string, outcome: VisitOutcome) => void;
  onChangeMany: (memberIds: string[], outcome: VisitOutcome) => void;
}

/** One roster block of the attendance panel — a group section or the individuals. */
const RosterAttendanceSection: React.FC<RosterAttendanceSectionProps> = ({
  section,
  outcomes,
  feeNames,
  awayMemberIds,
  onChange,
  onChangeMany,
}) => {
  const { t } = useTranslation();
  const sectionMemberIds = section.members.map((m) => m.memberId);
  const heading =
    section.kind === "group" ? section.groupName : t("sessions.attendance.individuals");

  return (
    <div className="rounded-xl border border-grey-90 p-3 dark:border-primary-20">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-grey-10 dark:text-grey-90">
          {heading}
          <span className="ml-2 font-normal text-grey-30 dark:text-grey-70">
            {section.members.length}
          </span>
        </p>
        {section.members.length > 0 && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onChangeMany(sectionMemberIds, VisitOutcome.Attended)}
              className="rounded-md px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30"
            >
              {t("sessions.attendance.groupAllAttended")}
            </button>
            <button
              type="button"
              onClick={() => onChangeMany(sectionMemberIds, VisitOutcome.CancelledCharged)}
              className="rounded-md px-2 py-1 text-xs font-medium text-grey-30 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
            >
              {t("sessions.attendance.groupAllAbsent")}
            </button>
          </div>
        )}
      </div>

      {section.members.length === 0 ? (
        <p className="mt-2 text-sm text-grey-40 dark:text-grey-60">
          {t("sessions.attendance.noMembers")}
        </p>
      ) : (
        <div className="mt-1 divide-y divide-grey-95 dark:divide-primary-20">
          {section.members.map((member) => (
            <AttendanceRow
              key={member.memberId}
              name={member.fullName}
              memberId={member.memberId}
              away={awayMemberIds?.has(member.memberId)}
              outcome={outcomes[member.memberId] ?? VisitOutcome.Unmarked}
              feeName={feeNames?.[member.memberId]}
              onChange={(outcome) => onChange(member.memberId, outcome)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RosterAttendanceSection;
